import React, { useState, useCallback } from 'react';
import { TranslationProvider, useTranslations } from './context/TranslationContext';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useIubendaConsent } from './hooks/useIubendaConsent';

// Import section components
import Header from './sections/Header';
import HeroSection from './sections/HeroSection';
import ProductsSection from './sections/ProductsSection';
import SolutionsSection from './sections/SolutionsSection';
import OurDataSourcesSection from './sections/OurDataSourcesSection';
import Footer from './sections/Footer';
import PrivacyPolicy from './pages/privacy';
import TermsAndConditions from './pages/terms';
import LegisPrivacy from './pages/legis-privacy';
import LegisTerms from './pages/legis-terms';
import SolutionsPage from './pages/solutions';
import AziendaPage from './pages/azienda';
import FAQPage from './pages/faq';
import LegisGraphPage from './pages/legisgraph';
import Doc2GraphPage from './pages/doc2graph';
import RedirectTo from './components/RedirectTo';
import Button from './components/Button';
import { Mail } from 'lucide-react';

import ScrollToTop from './components/ScrollToTop';

const AppContent: React.FC = () => {
  const { currentLanguage, t } = useTranslations();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    if (location.pathname !== '/') return;
    const title = currentLanguage === 'it' ? "LegisRatio - Potenzia l’AI con i grafi" : 'LegisRatio - Empower AI with knowledge graphs';
    const description = currentLanguage === 'it'
      ? 'Conoscenza giuridica strutturata per applicazioni e agenti AI, accessibile tramite API, plugin e knowledge graph.'
      : 'Structured legal knowledge for applications and AI agents, available through APIs, plugins and knowledge graphs.';
    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', description);
  }, [location.pathname, currentLanguage]);

  // Initialize iubenda consent management
  useIubendaConsent(currentLanguage);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return (
    <div className="font-sans antialiased overflow-x-hidden text-white bg-void-blue" lang={currentLanguage}>
      <ScrollToTop />
      <Header
        isMenuOpen={isMenuOpen}
        toggleMenu={toggleMenu}
        closeMenu={closeMenu}
      />

      <Routes>
        <Route path="/" element={
          <>
            <HeroSection />
            <ProductsSection />
            <OurDataSourcesSection />
            <SolutionsSection />

            <section className="relative border-t border-white/10 py-16 md:py-24 z-10 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-void-blue to-deep-space z-0"></div>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-4xl bg-gradient-radial from-cosmic-teal/10 to-transparent opacity-50 blur-3xl pointer-events-none"></div>

              <div className="container mx-auto px-4 md:px-6 lg:px-12 text-center max-w-4xl relative z-10">
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6 md:mb-8 text-white">
                  {t('solutions_cta_title')}
                </h2>
                <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-text-secondary">
                  {t('solutions_cta_subtitle')}
                </p>
                <p className="text-base md:text-lg mb-10 md:mb-12 text-text-muted">
                  {t('solutions_cta_description')}
                </p>

                <Button
                  size="lg"
                  variant="gradient"
                  icon={<Mail size={20} />}
                  onClick={() => {
                    const subject = encodeURIComponent(t("demo_email_subject"));
                    const body = encodeURIComponent(t("demo_email_body"));
                    window.location.href = `mailto:info@legisratio.it?subject=${subject}&body=${body}`;
                  }}
                  className="shadow-glow-teal hover:shadow-glow-teal-lg transition-shadow duration-300"
                >
                  {t('solutions_book_demo')}
                </Button>
              </div>
            </section>
          </>
        } />
        <Route path="/legischat" element={<SolutionsPage />} />
        <Route path="/legisgraph" element={<LegisGraphPage />} />
        <Route path="/doc2graph" element={<Doc2GraphPage />} />
        <Route path="/solutions" element={<RedirectTo to="/legischat" />} />
        <Route path="/azienda" element={<AziendaPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/legis-privacy" element={<LegisPrivacy />} />
        <Route path="/legis-terms" element={<LegisTerms />} />
      </Routes>

      <Footer />
    </div>
  );
};

const WrappedApp: React.FC = () => (
  <TranslationProvider>
    <AppContent />
  </TranslationProvider>
);

export default WrappedApp;